import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Camera,
  Calendar,
  CheckCircle2,
  KeyRound,
  Loader2,
  Mail,
  Phone,
  Save,
  ShieldCheck,
  Wallet,
  X,
} from "lucide-react";
import ResourcePersonNavBar from "../components/ResourcePersonNavBar";
import Toast from "../components/Toast";
import { imageUrl } from "../lib/format";
import {
  useResourcePersonAuth,
  useResourcePersonUploads,
} from "../store/resourcePerson/resourcePersonStore";

const inputClass =
  "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/10 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500";
const labelClass = "block text-xs font-semibold uppercase tracking-wide text-slate-500";

const avatarUrl = (value) =>
  imageUrl(value) || "/images/default_profile.png";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
};

const formatMoney = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 0,
      }).format(amount)
    : "—";
};

/**
 * /resource-person/account/settings
 *
 *   GET   /api/resource-person/me
 *   PATCH /api/resource-person/me   { name?, phone?, bio?, avatar? }
 *   POST  /api/resource-person/auth/change-password
 *
 * The profile schema only accepts name, phone, bio and avatar, so this page
 * edits exactly those fields. Everything else is read-only account info.
 */
const Profile = () => {
  const { fetchMe, updateMe, changePassword } = useResourcePersonAuth();
  const { uploadImage, imageUploadLoading, imageUploadError } = useResourcePersonUploads();

  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", bio: "", avatar: "" });
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [success, setSuccess] = useState(true);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [password, setPassword] = useState({ currentPassword: "", newPassword: "", confirm: "" });

  const notify = useCallback((message, ok = true) => {
    setToast(message);
    setSuccess(ok);
    setTimeout(() => setToast(null), 5000);
  }, []);

  const seed = useCallback((profile) => {
    setUser(profile);
    setForm({
      name: profile?.name || "",
      phone: profile?.phone || "",
      bio: profile?.bio || "",
      avatar: profile?.avatar || "",
    });
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const result = await fetchMe();
      if (!active) return;
      if (result.success) seed(result.user);
      else notify(result.error || "We couldn't load your profile.", false);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [fetchMe, notify, seed]);

  const set = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const handleAvatarFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const result = await uploadImage(file, "profile-avatars");
    if (!result.success) {
      notify(result.error || "Could not upload that image.", false);
      return;
    }
    const upload = result.upload || {};
    const url = upload.url || upload.secureUrl || upload.secure_url || upload.previewUrl;
    if (!url) {
      notify("The image uploaded but no URL was returned.", false);
      return;
    }
    setForm((current) => ({ ...current, avatar: url }));
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");
    const name = form.name.trim();
    if (name.length < 2) {
      setError("Please enter your full name.");
      return;
    }

    setSaving(true);
    const result = await updateMe({
      name,
      phone: form.phone.trim() || null,
      bio: form.bio.trim() || null,
      avatar: form.avatar.trim() || null,
    });
    setSaving(false);

    if (!result.success) {
      setError(result.error || "We couldn't save your profile.");
      return;
    }
    seed(result.user || { ...user, ...form });
    notify("Profile updated successfully.");
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    setPasswordError("");
    if (!password.currentPassword || !password.newPassword) {
      setPasswordError("Fill in your current and new password.");
      return;
    }
    if (password.newPassword.length < 6) {
      setPasswordError("Your new password must be at least 6 characters.");
      return;
    }
    if (password.newPassword !== password.confirm) {
      setPasswordError("The new passwords don't match.");
      return;
    }

    setPasswordSaving(true);
    const result = await changePassword({
      currentPassword: password.currentPassword,
      newPassword: password.newPassword,
    });
    setPasswordSaving(false);

    if (!result.success) {
      setPasswordError(result.error || "We couldn't change your password.");
      return;
    }
    setPassword({ currentPassword: "", newPassword: "", confirm: "" });
    setPasswordOpen(false);
    notify("Password changed successfully.");
  };

  const status = String(user?.status || "ACTIVE").toUpperCase();
  const isActive = status === "ACTIVE";
  const preview = avatarUrl(form.avatar || user?.avatar);

  return (
    <div className="min-h-screen bg-slate-50">
      <ResourcePersonNavBar title="Profile" subTitle="Account" />
      {toast ? <Toast message={toast} onClose={() => setToast(null)} isSuccess={success} /> : null}

      <main className="mx-auto max-w-6xl px-4 py-6 md:px-6">
        {loading && !user ? (
          <div className="flex min-h-[60vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#16213A]" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header */}
            <section className="flex flex-col gap-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                  <img src={preview} alt={form.name || "Profile"} className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0">
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-[#004ac6]">
                    Resource person account
                  </p>
                  <h1 className="m-0 mt-1 truncate text-2xl font-bold text-slate-900">
                    {form.name || "Your profile"}
                  </h1>
                  <p className="m-0 mt-1 truncate text-sm text-slate-500">{user?.email || "—"}</p>
                </div>
              </div>

              <span
                className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-100"
                    : "bg-rose-50 text-rose-700 ring-rose-100"
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${isActive ? "bg-emerald-500" : "bg-rose-500"}`} />
                {status}
              </span>
            </section>

            <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
              {/* Editable profile */}
              <form
                onSubmit={save}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="mb-5 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="m-0 text-lg font-semibold text-slate-900">Profile information</h2>
                    <p className="m-0 mt-1 text-sm text-slate-500">
                      This is the information shown to learners and moderators.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={imageUploadLoading}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
                  >
                    {imageUploadLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                    Change photo
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarFile}
                  />
                </div>

                {imageUploadError ? (
                  <p className="m-0 mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {imageUploadError}
                  </p>
                ) : null}

                {(error || user?.message) ? (
                  <p className="m-0 mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {error}
                  </p>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <label className={`${labelClass} sm:col-span-2`}>
                    Full name
                    <input
                      className={inputClass}
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Your full name"
                    />
                  </label>

                  <label className={labelClass}>
                    Email
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 mt-0.5 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        className={`${inputClass} pl-9`}
                        value={user?.email || ""}
                        readOnly
                        disabled
                      />
                    </div>
                  </label>

                  <label className={labelClass}>
                    Phone
                    <div className="relative">
                      <Phone className="pointer-events-none absolute left-3 top-1/2 mt-0.5 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        className={`${inputClass} pl-9`}
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="+234…"
                        inputMode="tel"
                      />
                    </div>
                  </label>

                  <label className={`${labelClass} sm:col-span-2`}>
                    Bio
                    <textarea
                      rows={5}
                      className={`${inputClass} resize-y`}
                      value={form.bio}
                      onChange={set("bio")}
                      placeholder="Tell learners a little about your background and expertise."
                    />
                  </label>

                  <label className={`${labelClass} sm:col-span-2`}>
                    Avatar URL
                    <input
                      className={inputClass}
                      value={form.avatar}
                      onChange={set("avatar")}
                      placeholder="https://…"
                    />
                  </label>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#16213A] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#203052] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" /> Save changes
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Account info */}
              <aside className="space-y-6">
                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h2 className="m-0 text-lg font-semibold text-slate-900">Account</h2>
                  <p className="m-0 mt-1 text-sm text-slate-500">
                    Read-only details for your resource person account.
                  </p>

                  <dl className="mt-5 space-y-4">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                        <ShieldCheck className="h-4 w-4" />
                      </span>
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Status
                        </dt>
                        <dd className="m-0 mt-0.5 text-sm font-semibold text-slate-800">{status}</dd>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                        <Wallet className="h-4 w-4" />
                      </span>
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Lifetime earnings
                        </dt>
                        <dd className="m-0 mt-0.5 text-sm font-semibold text-slate-800">
                          {formatMoney(user?.cumulativeEarnings)}
                        </dd>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                        <Calendar className="h-4 w-4" />
                      </span>
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Joined
                        </dt>
                        <dd className="m-0 mt-0.5 text-sm font-semibold text-slate-800">
                          {formatDate(user?.createdAt)}
                        </dd>
                      </div>
                    </div>
                  </dl>
                </section>

                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h2 className="m-0 text-lg font-semibold text-slate-900">Security</h2>
                  <p className="m-0 mt-1 text-sm text-slate-500">
                    Update the password you use to sign in.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setPasswordError("");
                      setPasswordOpen(true);
                    }}
                    className="mt-4 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    <KeyRound className="h-4 w-4" /> Change password
                  </button>
                </section>
              </aside>
            </div>
          </div>
        )}
      </main>

      {passwordOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={submitPassword}
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="m-0 text-lg font-semibold text-slate-900">Change password</h2>
                <p className="m-0 mt-1 text-sm text-slate-500">
                  Enter your current password, then choose a new one.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPasswordOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {passwordError ? (
              <p className="m-0 mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {passwordError}
              </p>
            ) : null}

            {[
              ["currentPassword", "Current password"],
              ["newPassword", "New password"],
              ["confirm", "Confirm new password"],
            ].map(([key, label]) => (
              <label key={key} className={`${labelClass} mb-3`}>
                {label}
                <input
                  type="password"
                  className={inputClass}
                  value={password[key]}
                  onChange={(event) =>
                    setPassword((current) => ({ ...current, [key]: event.target.value }))
                  }
                />
              </label>
            ))}

            <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => setPasswordOpen(false)}
                className="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={passwordSaving}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-[#16213A] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#203052] disabled:opacity-60"
              >
                {passwordSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Updating…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" /> Update password
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default Profile;
